import type { FC } from "react";

import { Controller, useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs";
import type { PlaceInputResults } from "@ui/components/PlaceInput";
import { PlaceInput } from "@ui/components/PlaceInput";
import { useTranslations } from "@ui/providers/useTranslations";

import type { LocationServiceAutocompleteService } from "@jti/fe-common";

import styles from "./SingleFieldLookupAddressForm.module.scss";

export type SingleFieldLookupAddressFormProps = {
  fieldName?(name: string): string;
  autocompleteProvider: LocationServiceAutocompleteService;
};

const fieldNameDefault = (name: string) => name;

export const SingleFieldLookupAddressForm: FC<SingleFieldLookupAddressFormProps> = ({
  fieldName = fieldNameDefault,
  autocompleteProvider = "daum",
}) => {
  const { __ } = useTranslations();
  const { setValue, watch, control, trigger } = useFormContext();
  const postcodeFilled = watch(fieldName("postcode"));

  const onAutocompleteChange = (place: PlaceInputResults) => {
    setValue(fieldName("city"), place.city);
    setValue(fieldName("streetName"), place.streetName);
    setValue(fieldName("streetNumber"), place.streetNumber);
    setValue(fieldName("postcode"), place.postcode);
    trigger();
  };

  return (
    <div>
      {postcodeFilled && (
        <Controller
          control={control}
          name={fieldName("postcode")}
          rules={{ required: __("address_fields.postcode_required") }}
          render={({ field }) => (
            <TextInput
              {...field}
              label={__("address_fields.postcode")}
              readOnly={true}
              containerClassName={styles.postcodeInput}
              data-testid="singleFieldAddressPostcodeInput"
              placeholder={__("address_fields.postcode_placeholder")}
              autoComplete="postal-code"
            />
          )}
        />
      )}

      <Controller
        control={control}
        name={fieldName("streetName")}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field }) => (
          <PlaceInput
            {...field}
            label={__("address_fields.full_address")}
            placeholder={__("address_fields.full_address_placeholder")}
            autocompleteProvider={autocompleteProvider}
            onPlace={onAutocompleteChange}
            autoComplete="street-address"
          />
        )}
      />
      {postcodeFilled && (
        <Controller
          control={control}
          name={fieldName("streetAdditional")}
          render={({ field }) => (
            <TextInput
              {...field}
              required={false}
              label={__("address_fields.remaining_address")}
              data-testid="singleFieldAddressRemainingInput"
              placeholder={__("address_fields.remaining_address_placeholder")}
              autoComplete="address-line2"
            />
          )}
        />
      )}
    </div>
  );
};
